/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslations } from '../hooks/useTranslations';
import CustomerHeader from '../components/CustomerHeader';
import '../App.css';
import './ServicesHomePage.css';
import './CustomerHomePage.css';

interface ServiceCategory {
  id: string;
  name: string;
  nameHindi: string;
  icon: string;
  color: string;
  gradient: string;
  description: string;
}

const ServicesHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const t = useTranslations();
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });
  const [cartCount, setCartCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  // Get cart count from localStorage
  useEffect(() => {
    const updateCartCount = () => {
      try {
        const cartData = localStorage.getItem('cart');
        const multiShopCart = localStorage.getItem('multiShopCart');
        let count = 0;
        
        if (cartData) {
          const cart = JSON.parse(cartData);
          count += Array.isArray(cart) ? cart.reduce((sum, item) => sum + (item.quantity || 1), 0) : 0;
        }
        
        if (multiShopCart) {
          const multiCart = JSON.parse(multiShopCart);
          if (Array.isArray(multiCart) && multiCart.length > 0) {
            // Check if it's the nested format: { shop, items: [{ product, quantity }] }
            if (multiCart[0]?.shop && multiCart[0]?.items) {
              // Nested structure: array of shop carts
              count += multiCart.reduce((sum: number, shopCart: any) => {
                if (shopCart.items && Array.isArray(shopCart.items)) {
                  return sum + shopCart.items.reduce((itemSum: number, item: any) => itemSum + (item.quantity || 1), 0);
                }
                return sum;
              }, 0);
            } else if (multiCart[0]?.product || multiCart[0]?.quantity !== undefined) {
              // Flat structure: array of { product, shop, quantity }
              count += multiCart.reduce((sum: number, item: any) => sum + (item.quantity || 1), 0);
            }
          }
        }
        
        setCartCount(count);
      } catch (error) {
        console.error('Error reading cart:', error);
      }
    };

    updateCartCount();
    const interval = setInterval(updateCartCount, 1000);
    return () => clearInterval(interval);
  }, []);

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = (event: CustomEvent) => {
      setLanguage(event.detail.language);
    };
    window.addEventListener('languageChanged', handleLanguageChange as EventListener);
    return () => window.removeEventListener('languageChanged', handleLanguageChange as EventListener);
  }, []);

  // Update document language attribute when language changes
  useEffect(() => {
    document.documentElement.lang = language;
    localStorage.setItem('language', language);
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language } }));
  }, [language]);

  // Service categories with India-first design
  const serviceCategories: ServiceCategory[] = [
    // Home & Repair Services
    {
      id: 'plumbing',
      name: 'Plumbing',
      nameHindi: 'प्लंबिंग',
      icon: '🔧',
      color: '#2563eb',
      gradient: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
      description: 'Pipe repair, installation, water issues'
    },
    {
      id: 'electrical',
      name: 'Electricals',
      nameHindi: 'इलेक्ट्रिकल',
      icon: '⚡',
      color: '#f59e0b',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      description: 'Wiring, switches, electrical repair'
    },
    {
      id: 'painting',
      name: 'Painting',
      nameHindi: 'पेंटिंग',
      icon: '🎨',
      color: '#10b981',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      description: 'Home painting, wall colors'
    },
    {
      id: 'carpenter',
      name: 'Carpenter',
      nameHindi: 'बढ़ई',
      icon: '🪚',
      color: '#8b5cf6',
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      description: 'Furniture, doors, windows'
    },
    {
      id: 'mason',
      name: 'Mason / Building',
      nameHindi: 'राजमिस्त्री',
      icon: '🧱',
      color: '#ef4444',
      gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      description: 'Construction, building work'
    },
    {
      id: 'tiles-flooring',
      name: 'Tiles & Flooring',
      nameHindi: 'टाइल्स',
      icon: '🔲',
      color: '#06b6d4',
      gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
      description: 'Tile installation, flooring'
    },
    {
      id: 'waterproofing',
      name: 'Waterproofing',
      nameHindi: 'वॉटरप्रूफिंग',
      icon: '💧',
      color: '#3b82f6',
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      description: 'Roof, terrace waterproofing'
    },
    {
      id: 'pest-control',
      name: 'Pest Control',
      nameHindi: 'कीट नियंत्रण',
      icon: '🐛',
      color: '#84cc16',
      gradient: 'linear-gradient(135deg, #84cc16 0%, #65a30d 100%)',
      description: 'Rodent, insect control'
    },
    {
      id: 'ac-repair',
      name: 'AC / Fridge Repair',
      nameHindi: 'एसी/फ्रिज रिपेयर',
      icon: '❄️',
      color: '#0ea5e9',
      gradient: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
      description: 'AC, fridge, washing machine'
    },
    {
      id: 'ro-service',
      name: 'RO Water Service',
      nameHindi: 'आरओ सर्विस',
      icon: '💦',
      color: '#14b8a6',
      gradient: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
      description: 'RO purifier service & repair'
    },
    // Events & Decor
    {
      id: 'event-decor',
      name: 'Event Decorators',
      nameHindi: 'इवेंट डेकोरेटर',
      icon: '🎉',
      color: '#ec4899',
      gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
      description: 'Wedding, party decoration'
    },
    {
      id: 'tent-stage',
      name: 'Tent & Stage',
      nameHindi: 'तंबू और स्टेज',
      icon: '⛺',
      color: '#f97316',
      gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
      description: 'Tent setup, stage decoration'
    },
    // Transport & Logistics
    {
      id: 'drivers',
      name: 'Drivers',
      nameHindi: 'ड्राइवर',
      icon: '🚗',
      color: '#6366f1',
      gradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
      description: 'Auto, cab, local drivers'
    },
    {
      id: 'transport',
      name: 'Goods Transport',
      nameHindi: 'माल परिवहन',
      icon: '🚚',
      color: '#f59e0b',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      description: 'Mini truck, pickup, goods'
    },
    {
      id: 'packers-movers',
      name: 'Packers & Movers',
      nameHindi: 'पैकर्स एंड मूवर्स',
      icon: '📦',
      color: '#8b5cf6',
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      description: 'Shifting, packing services'
    },
    // Daily Wage & Helpers
    {
      id: 'construction-labor',
      name: 'Construction Labor',
      nameHindi: 'निर्माण मजदूर',
      icon: '👷',
      color: '#ef4444',
      gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      description: 'Daily wage workers'
    },
    {
      id: 'house-helpers',
      name: 'House Helpers',
      nameHindi: 'घरेलू सहायक',
      icon: '🏠',
      color: '#10b981',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      description: 'Home helpers, assistants'
    },
    {
      id: 'loaders',
      name: 'Loaders / Unloaders',
      nameHindi: 'लोडर',
      icon: '📦',
      color: '#f59e0b',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      description: 'Loading, unloading work'
    },
    {
      id: 'security',
      name: 'Security Guards',
      nameHindi: 'सुरक्षा गार्ड',
      icon: '🛡️',
      color: '#6366f1',
      gradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
      description: 'Watchmen, security'
    },
    // Cleaning & Maintenance
    {
      id: 'home-cleaning',
      name: 'Home Cleaning',
      nameHindi: 'घर की सफाई',
      icon: '🧹',
      color: '#06b6d4',
      gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
      description: 'Deep cleaning, housekeeping'
    },
    {
      id: 'tank-cleaning',
      name: 'Tank Cleaning',
      nameHindi: 'टैंक सफाई',
      icon: '💧',
      color: '#3b82f6',
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      description: 'Water tank, overhead tank'
    },
    {
      id: 'sofa-cleaning',
      name: 'Sofa & Carpet',
      nameHindi: 'सोफा सफाई',
      icon: '🛋️',
      color: '#8b5cf6',
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      description: 'Sofa, carpet cleaning'
    },
    // Digital & Business
    {
      id: 'computer-repair',
      name: 'Computer Repair',
      nameHindi: 'कंप्यूटर रिपेयर',
      icon: '💻',
      color: '#6366f1',
      gradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
      description: 'PC, laptop repair'
    },
    {
      id: 'cctv',
      name: 'CCTV Installation',
      nameHindi: 'सीसीटीवी इंस्टालेशन',
      icon: '📹',
      color: '#ef4444',
      gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      description: 'CCTV camera setup'
    },
    {
      id: 'wifi-setup',
      name: 'Wi-Fi Setup',
      nameHindi: 'वाई-फाई सेटअप',
      icon: '📶',
      color: '#10b981',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      description: 'Internet, router setup'
    },
    // Personal Services
    {
      id: 'beautician',
      name: 'Beautician',
      nameHindi: 'ब्यूटीशियन',
      icon: '💄',
      color: '#ec4899',
      gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
      description: 'Makeup, beauty services'
    },
    {
      id: 'tailor',
      name: 'Tailor',
      nameHindi: 'दर्जी',
      icon: '✂️',
      color: '#f97316',
      gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
      description: 'Stitching, alterations'
    },
    {
      id: 'laundry',
      name: 'Laundry / Ironing',
      nameHindi: 'कपड़े धोना',
      icon: '👔',
      color: '#0ea5e9',
      gradient: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
      description: 'Washing, ironing clothes'
    }
  ];

  const handleCategoryClick = (categoryId: string) => {
    navigate(`/services/${categoryId}`);
  };

  const handleEmergencyService = () => {
    navigate('/services/emergency');
  };

  const handleProviderOnboard = () => {
    navigate('/services/onboard');
  };

  const location = useLocation();
  const isServicesPage = location.pathname === '/services' || location.pathname.startsWith('/services/');

  return (
    <div className="services-home-page">
      {/* Header - Same as Browse All page */}
      <CustomerHeader
        showBackButton
        showLoyaltyButton
        cartCount={cartCount}
        onSearch={(query) => {
          navigate(`/search?q=${encodeURIComponent(query)}`);
        }}
        onCartClick={() => navigate('/app', { state: { showCart: true } })}
      />

      {/* Navigation Buttons */}
      <nav id="navigation" className="main-nav" role="navigation" aria-label="Primary">
        <div className="nav-content">
          <div className="nav-buttons">
            <button 
              className="nav-btn" 
              onClick={() => navigate('/app')}
            >
              🏠 {t.mainStore || 'Dashboard'}
            </button>
            <button 
              className="nav-btn" 
              onClick={() => navigate('/explore-shops')}
            >
              🔎 {t.exploreShops || 'Explore Shops'}
            </button>
            <button 
              className="nav-btn" 
              onClick={() => navigate('/browse-all')}
            >
              🛍️ {t.browseAll || 'Browse All'}
            </button>
            <button 
              className={`nav-btn ${isServicesPage ? 'active' : ''}`}
              onClick={() => navigate('/services')}
            >
              🔧 {language === 'hi' ? 'सेवाएं' : language === 'te' ? 'సేవలు' : language === 'ta' ? 'சேவைகள்' : 'Services'}
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="services-main-content">
        {/* Hero Section */}
        <section className="services-hero">
          <div className="services-hero-content">
            <h1 className="services-hero-title">
              {language === 'hi' ? 'स्थानीय सेवाएं खोजें' : 'Find Local Services'}
            </h1>
            <p className="services-hero-subtitle">
              {language === 'hi' 
                ? 'विश्वसनीय पेशेवरों को खोजें और बुक करें' 
                : 'Discover & book trusted professionals'}
            </p>
            <div className="services-hero-actions">
              <button
                className="services-emergency-btn"
                onClick={handleEmergencyService}
                aria-label="Emergency Service"
              >
                <span className="emergency-icon">🚨</span>
                <span>{language === 'hi' ? 'आपातकालीन सेवा' : 'Emergency Service'}</span>
              </button>
            </div>
          </div>
        </section>

        {/* Service Categories Grid */}
        <section className="services-categories-section">
          <div className="services-container">
            <h2 className="services-section-title">
              {language === 'hi' ? 'सेवा श्रेणियां' : 'Service Categories'}
            </h2>
            <div className="services-categories-grid">
              {serviceCategories.map((category) => (
                <button
                  key={category.id}
                  className="service-category-card"
                  onClick={() => handleCategoryClick(category.id)}
                  style={{ '--category-color': category.color } as React.CSSProperties}
                  aria-label={`${category.name} services`}
                >
                  <div 
                    className="service-category-icon"
                    style={{ background: category.gradient }}
                  >
                    <span className="service-icon-emoji">{category.icon}</span>
                  </div>
                  <div className="service-category-info">
                    <h3 className="service-category-name">
                      {language === 'hi' ? category.nameHindi : category.name}
                    </h3>
                    <p className="service-category-desc">{category.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Provider Onboarding CTA */}
        <section className="services-onboard-section">
          <div className="services-container">
            <div className="services-onboard-card">
              <div className="services-onboard-content">
                <h3 className="services-onboard-title">
                  {language === 'hi' ? 'सेवा प्रदाता बनें' : 'Become a Service Provider'}
                </h3>
                <p className="services-onboard-desc">
                  {language === 'hi'
                    ? 'अपनी सेवाएं सूचीबद्ध करें और ग्राहकों तक पहुंचें'
                    : 'List your services and reach customers'}
                </p>
                <button
                  className="services-onboard-btn"
                  onClick={handleProviderOnboard}
                >
                  {language === 'hi' ? 'अभी शुरू करें' : 'Get Started'}
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ServicesHomePage;

